/**
 * `CommsApp` — the **COMMS.app** body: the game-scoped chat & activity feed, the shell's fixed
 * right column (`decisions/0203` point 4). It renders the one **interleaved**
 * stream of chat lines, system events and `/roll` dice cards, plus an input box to post a line.
 * What the feed shows is the filter menu's choice (`CommsFilterMenu`, in the column header).
 *
 * It reads the feed + `postChat` straight off the app-wide Game session
 * ({@link useGameSessionContext}) — the same `/ws/game` socket the rest of the shell rides —
 * so the window is just a view: the server stamps identity + persistence, COMMS only renders and
 * sends. Display names resolve **client-side** off `session.slots` (the slot id → callsign / "GM"),
 * mirroring the wire rule that the author is a server-stamped slot id, never a client-asserted name.
 *
 * Diegetic OMNINET voice: the feed is the lance's comms channel; the window chrome reads as
 * terminal/system output.
 */
import { faRotateLeft, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../lib/Icon";
import { Fragment, useCallback, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { MAX_CHAT_TEXT_LENGTH, type FeedEntry, type GameDecisionMessage, type PromptOption, type RollFeedEntry, type SystemFeedEntry } from "@lancer-vtt/shared";
import type { ResolutionAbout, ResolvedStep } from "@lancer-vtt/domain";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { useStage } from "./shell/stageContext";
import { useSlotNames } from "../../lib/game/useSlotNames";
import { ResultCard, buildShotView } from "../../lib/encounter/ResultCard";
import { AttackGroupCard, type ShotView } from "../../lib/encounter/AttackCard";
import { useInputHistory } from "../../lib/useInputHistory";
import { CommsDice } from "./CommsDice";
import { DecisionCard } from "./DecisionCard";
import { FEED_FILTER_KINDS, defaultFeedFilter, filterFeed, isDefaultFeedFilter } from "./commsFilter";
import { useFeedFilter } from "./CommsFilterMenu";
import { emoteLineParts } from "./emoteLine";
import { groupShots } from "./commsShotGroups";
import { undoneResolutionIds } from "./commsUndo";
import { useFeedHold } from "../../lib/dice/useFeedHold";
import { NotSentNote, useNotSentNote } from "../../lib/linkHealth";
import "./comms-app.css";

/**
 * How close to the end still counts as "at the bottom" for the stick-to-bottom rule below — one
 * wrapped feed line (~54px in COMBAT.app's 320px comms sidebar) plus its 6px inter-line gap. Wide
 * enough that a reader who nudged the wheel a notch keeps getting new lines pinned, and far narrower
 * than the feed viewport (~700px there), so a deliberate scroll back into history always lands
 * outside it.
 */
const NEAR_BOTTOM_PX = 64;

export function CommsApp({
  answerDecision,
  onHoverDecisionOption,
}: {
  /**
   * Threaded down from `CombatApp`'s `useEncounter(...)` controller — present ONLY on COMBAT's own
   * COMMS (portalled into the shell's column while COMBAT holds the stage), where a live
   * `/ws/encounter` socket exists to re-submit a decision-card answer. `undefined` on the shell's
   * own COMMS (a narrative stage, no encounter controller) — the `DecisionCard` then tells a
   * decider to respond once the fight is on the stage instead of rendering option buttons.
   */
  answerDecision?: (promptId: string, optionId: string) => "sent" | "gone" | "offline";
  /** A decision card's hovered option, for the board's highlight — `CombatApp` only. */
  onHoverDecisionOption?: (decision: GameDecisionMessage, option: PromptOption | null) => void;
} = {}) {
  const session = useGameSessionContext();
  const { feed, postChat, actorId, decisions, feedBaselineTs } = session;
  const { notSent, report } = useNotSentNote();
  // The GM's sheet roll buttons seed the dice composer here (`shell/railDice.ts`); null for a player
  // and outside the shell.
  const commsDice = useStage()?.commsDice ?? null;
  const pendingDecisions = Object.values(decisions);
  // The header menu's selection; session-local (no persistence across reloads by design).
  const { selected: filter, setSelected: setFilter } = useFeedFilter();
  const [draft, setDraft] = useState("");
  // Terminal-style Up/Down recall over this client's own sent messages, session-local only —
  // see `useInputHistory` for the pure push/navigate contract.
  const inputHistory = useInputHistory();
  const scrollRef = useRef<HTMLDivElement>(null);
  // The feed's inner content column — observed for height changes (see the ResizeObserver below).
  const contentRef = useRef<HTMLDivElement>(null);
  // Stick-to-bottom: like a good chat app, new lines auto-scroll into view, but only while the
  // reader is already parked at the bottom — scroll up to read history and we stop yanking them down.
  const stuckToBottom = useRef(true);
  // The scroll position the previous scroll event reported — the only way to tell a reader moving the
  // viewport up from content growing underneath it (see `onScroll`).
  const lastScrollTop = useRef(0);

  // slot id → display name (callsign / "GM"), resolved client-side off the live slot presence.
  // `useSlotNames` is the one shared resolver — the board's cursor labels use the same hook, so
  // there is only ever one copy of this mapping.
  const nameOf = useSlotNames();

  // Roll outcomes whose dice are still in the air. Without the hold a result card appears the
  // instant its broadcast lands — the earliest of the three disclosures, since unlike the board
  // it waits on no animation — so the player reads the damage number and only then watches the
  // dice settle. Held cards are withheld here, at the render seam, and nowhere else: the feed
  // itself is untouched, so this is pure presentation and leaves the append-only event stream
  // exactly as it was. Chat lines are never held.
  const heldEntryIds = useFeedHold(feed, feedBaselineTs);
  const shown = filterFeed(feed, filter).filter((entry) => !heldEntryIds.has(entry.id));
  const hasContent = shown.length > 0;
  // Held cards are excluded here too, so a feed whose only entry is mid-hold reads "OMNINET QUIET"
  // rather than blaming the filter for hiding something the filter has nothing to do with.
  const hasVisibleEntries = filterFeed(feed, new Set(FEED_FILTER_KINDS)).filter((entry) => !heldEntryIds.has(entry.id)).length > 0;
  // An empty feed reads three ways, and the line has to say which: nothing has happened yet; the
  // defaults hide everything so far (only the activity log has entries); or the reader's own choice
  // hides everything. The last two carry the one-click way out.
  const atDefaults = isDefaultFeedFilter(filter);

  // Pin the feed to its last line. Reads `scrollHeight` live, so the landing point is the feed's
  // true content bottom — independent of the input bar (a sibling *outside* the scroller, so it never
  // factors into this math).
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Keep the feed stuck to the bottom whenever its CONTENT changes height — but only while the reader
  // is parked there. A ResizeObserver on the inner content (rather than an effect on `shown.length`)
  // is what makes the landing exact: a posted line lands via the server broadcast, a combat result
  // card finishes its layout, or the mono font swaps in — each grows the content *after* React's
  // commit, and re-pins here. A single post-time scroll would fire against the pre-reflow
  // `scrollHeight` and land a few px short. The stuck guard means scrolling up to read history is
  // never yanked back down; the observer also pins the first fill and every filter switch (both
  // change the content height).
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      if (stuckToBottom.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [hasContent, scrollToBottom]);

  // Track whether the reader is parked at the bottom. The subtlety that makes this more than a
  // distance check: a scroll event is NOT delivered in the frame that caused it, and per the HTML
  // rendering steps a frame fires its scroll events BEFORE it delivers ResizeObserver callbacks. So
  // the event from the pin above arrives a frame later — by which time the next broadcast may already
  // have grown the content — and a plain distance read would see a full entry of slack and conclude
  // the reader had scrolled away, latching the feed off the bottom for the rest of the session —
  // "auto-scroll works, until a second line lands in the same frame window."
  //
  // So distance only ever RE-sticks. UN-sticking additionally requires `scrollTop` to have moved up,
  // which only a reader can do: growing content raises `scrollHeight`, never `scrollTop`.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const previousTop = lastScrollTop.current;
    lastScrollTop.current = el.scrollTop;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX) {
      stuckToBottom.current = true;
      return;
    }
    if (el.scrollTop < previousTop) stuckToBottom.current = false;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (text === "") return;
    // A refused post (link down) keeps the draft and shows the note; nothing is queued.
    if (!report(postChat(text))) return;
    // Capture what THIS client sent, at the send site — never from the feed (other players'
    // messages must never enter this client's recall history).
    inputHistory.push(text);
    setDraft("");
    // Posting always jumps you to your own new line: re-pin now, and the ResizeObserver above pins
    // again the moment the server broadcast appends the line, so it lands exactly at the bottom.
    stuckToBottom.current = true;
    scrollToBottom();
  };

  // Terminal-style history recall: Up/Down are safe to intercept unconditionally here — this is a
  // single-line `<input>`, which has no native line navigation for the keys to fight over.
  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setDraft(inputHistory.up(draft));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setDraft(inputHistory.down(draft));
    }
  };

  return (
    <div className="comms">
      <div className="comms-feed" ref={scrollRef} onScroll={onScroll}>
        {hasContent ? (
          <div className="comms-feed-inner" ref={contentRef}>
            <FeedList entries={shown} nameOf={nameOf} selfSlotId={actorId} undone={undoneResolutionIds(feed)} />
          </div>
        ) : (
          <div className="comms-empty">
            {!hasVisibleEntries ? (
              "— OMNINET QUIET —"
            ) : atDefaults ? (
              <>
                Everything so far is in the activity log.{" "}
                <button type="button" className="comms-empty-link" onClick={() => setFilter(new Set(FEED_FILTER_KINDS))}>
                  Show it
                </button>
              </>
            ) : (
              <>
                Everything is filtered out.{" "}
                <button type="button" className="comms-empty-link" onClick={() => setFilter(defaultFeedFilter())}>
                  Show the defaults
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* The pinned decision-card region: any live, unresolved `game_decision`(s) stay fixed
          here — outside the scrolling feed above, so they
          never scroll away as new lines arrive — until their `game_decision_resolved` clears them
          (see `GameSession.decisions`). Holds zero or more cards; renders nothing when empty. */}
      {pendingDecisions.length > 0 && (
        <div className="comms-decisions">
          {pendingDecisions.map((decision) => (
            <DecisionCard key={decision.promptId} decision={decision} answerDecision={answerDecision} {...(onHoverDecisionOption !== undefined ? { onHoverOption: onHoverDecisionOption } : {})} />
          ))}
        </div>
      )}

      <form className="comms-input" onSubmit={onSubmit}>
        <input
          type="text"
          className="comms-text"
          placeholder="Type a message…"
          aria-label="Chat message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onInputKeyDown}
          disabled={actorId === null}
          maxLength={MAX_CHAT_TEXT_LENGTH}
        />
        <CommsDice preselect={commsDice} />
        <button type="submit" className="comms-send" disabled={actorId === null || draft.trim() === ""}>
          SEND
        </button>
      </form>
      <NotSentNote show={notSent} />
    </div>
  );
}

/**
 * The visible feed's lines, with a multi-shot action's entries grouped under one card
 * (`commsShotGroups.ts`). Exported so the grouping renders under test without a session provider.
 */
export function FeedList({
  entries,
  nameOf,
  selfSlotId,
  undone = NONE_UNDONE,
}: {
  entries: readonly FeedEntry[];
  nameOf: (slotId: string) => string;
  selfSlotId: string | null;
  /**
   * The resolution ids whose cards are currently undone (`commsUndo.ts`), folded from the WHOLE
   * feed rather than `entries`, so a card still strikes when its undo line is filtered off.
   */
  undone?: ReadonlySet<string>;
}) {
  return (
    <>
      {groupShots(entries).map((item) =>
        item.kind === "group" ? (
          <ShotGroup key={item.shots[0]!.id} shots={item.shots} nameOf={nameOf} selfSlotId={selfSlotId} undone={undone} />
        ) : (
          <FeedLine key={item.entry.id} entry={item.entry} nameOf={nameOf} selfSlotId={selfSlotId} undone={undone} />
        ),
      )}
    </>
  );
}

const NONE_UNDONE: ReadonlySet<string> = new Set();

/** One rendered feed entry, styled per kind. */
function FeedLine({
  entry,
  nameOf,
  selfSlotId,
  undone,
}: {
  entry: FeedEntry;
  nameOf: (slotId: string) => string;
  selfSlotId: string | null;
  undone: ReadonlySet<string>;
}) {
  if (entry.kind === "system") {
    // A bridged combat resolution carries the recorded steps + a combatant-name map, rendered
    // with the combat `ResultCard`. Presence events never pass the filter; command activity is
    // the deliberately opt-in TABLE stream.
    if (entry.resolution !== undefined) {
      return <ResolutionCard entry={entry} struck={undone.has(entry.resolution.id)} />;
    }
    // A private refusal — a malformed `/roll` (`roll_error`) or a non-GM hitting one of the nine
    // GM-only gates (`refused`) — is unfilterable and never persisted, so it
    // reads as its own thing rather than a durable system line: error-toned, dashed rather than
    // solid, to look as transient as it actually is (a reload loses it). Same class of thing, same
    // treatment — widen this branch rather than adding a near-identical second one.
    if (entry.event === "roll_error" || entry.event === "refused") {
      return (
        <div className="comms-line comms-line-refusal" role="alert">
          <Icon icon={faTriangleExclamation} className="comms-refusal-mark" /> {entry.text}
        </div>
      );
    }
    // A resolved-decision outcome line (`event: "decision"`) — the durable record of a
    // reaction/checkpoint answer ("what was asked → what was chosen"). Same muted system
    // treatment, its own mark so the scrollback distinguishes it from machine narration.
    if (entry.event === "undo" || entry.event === "redo") {
      return (
        <div className="comms-line comms-line-system comms-line-undo">
          <Icon icon={faRotateLeft} className="comms-sys-mark" /> {entry.text}
        </div>
      );
    }
    if (entry.event === "decision") {
      return (
        <div className="comms-line comms-line-system comms-line-decision">
          <span className="comms-sys-mark">⁇</span> {entry.text}
        </div>
      );
    }
    return (
      <div className="comms-line comms-line-system">
        <span className="comms-sys-mark">›</span> {entry.text}
      </div>
    );
  }
  if (entry.kind === "roll") {
    return <RollCard entry={entry} nameOf={nameOf} />;
  }
  if (entry.kind === "emote") {
    // Split, not one opaque string: the glyph gets its own span carrying a colour-emoji font
    // stack (`.comms-emote-glyph`), so it
    // stops inheriting the mono stack's fallback to a text-presentation glyph tinted by
    // `.comms-line-emote`'s accent color.
    const { text, emoji } = emoteLineParts(entry, nameOf(entry.authorSlotId));
    return (
      <div className="comms-line comms-line-emote">
        {text}
        <span className="comms-emote-glyph">{emoji}</span>
      </div>
    );
  }
  const mine = entry.authorSlotId === selfSlotId;
  return (
    <div className={`comms-line comms-line-chat${mine ? " mine" : ""}`}>
      <span className="comms-author">{nameOf(entry.authorSlotId)}</span>
      <span className="comms-body">{entry.text}</span>
    </div>
  );
}

/**
 * A bridged **combat-resolution card** — a system feed entry the server bridged from an encounter
 * resolution, rendered with the combat {@link ResultCard}. The recorded `steps` are read
 * straight off the entry (the domain narrows the wire's `unknown[]` to `ResolvedStep[]`, exactly like
 * the encounter-change witness); combatant ids resolve via the server-stamped `names` map shipped with
 * the entry (COMMS.app has no encounter state of its own). `about` (resolution-narration 1.1) is the
 * narrator's only source for who acted, with what, at whom — narrowed here from the wire's structural
 * mirror to `@lancer-vtt/domain`'s `ResolutionAbout` exactly like `steps`; absent on an old entry or a
 * resolution kind that never populated it, which the narrator degrades for rather than crashing on.
 */
function ResolutionCard({ entry, struck }: { entry: SystemFeedEntry; struck: boolean }) {
  const res = entry.resolution!;
  const names = res.names;
  const resolveName = (id: string): string => names[id] ?? id;
  return (
    <div className={`comms-line comms-line-resolution${struck ? ` ${UNDONE_CLASS}` : ""}`}>
      <ResultCard
        steps={res.steps as ResolvedStep[]}
        nameOf={resolveName}
        kind={res.kind}
        about={res.about as ResolutionAbout | undefined}
        warnings={res.warnings}
      />
    </div>
  );
}

/** The class an undone card carries: dimmed and struck through (`comms-app.css`). */
const UNDONE_CLASS = "comms-undone";

/** One bridged resolution entry as the attack card's view — `undefined` when it holds no attack roll. */
function shotViewOf(entry: SystemFeedEntry): ShotView | undefined {
  const res = entry.resolution!;
  return buildShotView({
    steps: res.steps as ResolvedStep[],
    nameOf: (id) => res.names[id] ?? id,
    kind: res.kind,
    about: res.about as ResolutionAbout | undefined,
    warnings: res.warnings,
  });
}

/**
 * A multi-shot action's entries (`commsShotGroups.ts` decided they belong together) under one
 * {@link AttackGroupCard}. A shot with no attack roll can't join the header, so a group that holds
 * one falls back to rendering each entry on its own.
 */
function ShotGroup({
  shots,
  nameOf,
  selfSlotId,
  undone,
}: {
  shots: readonly SystemFeedEntry[];
  nameOf: (slotId: string) => string;
  selfSlotId: string | null;
  undone: ReadonlySet<string>;
}) {
  const views = shots.map(shotViewOf);
  if (views.some((v) => v === undefined)) {
    return (
      <>
        {shots.map((entry) => (
          <FeedLine key={entry.id} entry={entry} nameOf={nameOf} selfSlotId={selfSlotId} undone={undone} />
        ))}
      </>
    );
  }
  // Each shot row carries `comms-line-resolution`, the class every lone resolution card's line
  // carries: one per feed entry, grouped or not, so "the Nth resolution card" still means the Nth
  // resolution entry.
  // An undone shot is struck and leaves the header's damage sum; once every shot is undone the
  // header is struck too, so no live-looking total survives a fully walked-back Barrage.
  const isUndone = (i: number) => undone.has(shots[i]!.resolution!.id);
  const allUndone = shots.every((_, i) => isUndone(i));
  return (
    <div className="comms-line comms-line-resolution-group">
      <AttackGroupCard
        views={views as ShotView[]}
        shotClassName={(i) => (isUndone(i) ? `comms-line-resolution ${UNDONE_CLASS}` : "comms-line-resolution")}
        isUndone={isUndone}
        {...(allUndone ? { headClassName: UNDONE_CLASS } : {})}
      />
    </div>
  );
}

/**
 * A `/roll` **dice card** — a pure render of a recorded {@link RollFeedEntry}, in
 * the terminal card idiom of the combat `ResultCard`: a headline (who rolled what), the
 * recorded die faces (dropped dice dimmed + struck), and the emphasized total. Every number
 * is read straight off the server-recorded entry — the card never re-rolls.
 *
 * A `/roll` is **compound**, so the faces render one run per recorded group, separated by the
 * group's own operator (`1d20 + 2d6` shows the d20's face, a `+`, then the two d6 faces) — that
 * separator is the only thing telling a reader which pool a face came from, and which pool was
 * subtracted. Flat `±<int>` terms have no faces to show; they're visible in the echoed
 * `notation` and folded into the total.
 */
function RollCard({ entry, nameOf }: { entry: RollFeedEntry; nameOf: (slotId: string) => string }) {
  return (
    <div className="comms-line comms-line-roll" role="status">
      <div className="comms-roll-head">
        <span className="comms-author">{nameOf(entry.authorSlotId)}</span> rolled{" "}
        {entry.label !== undefined && <span className="comms-roll-label">{entry.label} </span>}
        <span className="comms-roll-notation">{entry.notation}</span>
      </div>
      <div className="comms-roll-body">
        <span className="comms-roll-dice">
          {entry.groups.map((group, g) => (
            <Fragment key={g}>
              {g > 0 || group.sign < 0 ? <span className="comms-roll-op">{group.sign < 0 ? "−" : "+"}</span> : null}
              {group.draws.map((face, i) => (
                <span key={i} className={`comms-die${group.kept[i] ? "" : " dropped"}`}>
                  {face}
                </span>
              ))}
            </Fragment>
          ))}
        </span>
        <span className="comms-roll-total">= {entry.total}</span>
      </div>
    </div>
  );
}
